// Small shared building blocks for the sign-in / admin / history screens,
// built accessible from the start: real <label>s tied to inputs, dialogs
// that announce themselves to screen readers, trap Tab inside, close on
// Escape, and hand focus back to whatever opened them.
import { T, X } from "../theme.jsx";

const { useEffect, useRef, useId } = React;

const btnBase = { border: "none", borderRadius: 8, padding: "9px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };
const BTN = {
  primary: { ...btnBase, background: T.accent, color: T.accentText },
  secondary: { ...btnBase, background: T.panel2, color: T.text, border: `1px solid ${T.border}` },
  danger: { ...btnBase, background: T.danger, color: "#fff" },
  ghost: { ...btnBase, background: "transparent", color: T.text, border: `1px solid ${T.border}` },
};

function Button({ variant = "primary", disabled, style, children, ...rest }) {
  return (
    <button className="rb-btn" type="button" disabled={disabled}
      style={{ ...BTN[variant], ...(disabled ? { opacity: 0.55, cursor: "default" } : null), ...style }} {...rest}>
      {children}
    </button>
  );
}

const inputStyle = {
  width: "100%", boxSizing: "border-box", fontSize: 14, border: `1px solid ${T.border}`, borderRadius: 8,
  padding: "10px 12px", background: "#fff", color: T.text, fontFamily: "inherit",
};

function Field({ label, hint, error, children, inputProps }) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  return (
    <div style={{ marginBottom: 12, textAlign: "left" }}>
      <label htmlFor={id} style={{ display: "block", fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 5 }}>{label}</label>
      {children
        ? children({ id, "aria-describedby": [hintId, errId].filter(Boolean).join(" ") || undefined, "aria-invalid": error ? true : undefined })
        : <input id={id} className="rb-input" style={inputStyle} aria-describedby={[hintId, errId].filter(Boolean).join(" ") || undefined}
            aria-invalid={error ? true : undefined} {...inputProps} />}
      {hint && <div id={hintId} style={{ fontSize: 12, color: T.subtext, marginTop: 4 }}>{hint}</div>}
      {error && <div id={errId} role="alert" style={{ fontSize: 12.5, color: T.danger, marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function Notice({ tone = "danger", children }) {
  const colors = tone === "danger" ? [T.dangerBg, T.danger] : tone === "ok" ? ["#D9EBD9", "#1E5B2A"] : [T.infoBg, T.infoText];
  return (
    <div role={tone === "danger" ? "alert" : "status"} style={{ background: colors[0], color: colors[1], borderRadius: 8, padding: "9px 12px", fontSize: 13, marginBottom: 12, textAlign: "left" }}>
      {children}
    </div>
  );
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function Modal({ title, onClose, children, width = 560, closeLabel = "Close" }) {
  const panelRef = useRef(null);
  const titleId = useId();
  useEffect(() => {
    const opener = document.activeElement;
    const panel = panelRef.current;
    // start in the first field if there is one (so typing works at once),
    // otherwise on the first button
    const first = panel && (panel.querySelector("input:not([type=hidden]):not([disabled]), textarea, select") || panel.querySelector(FOCUSABLE));
    (first || panel).focus();
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose && onClose(); return; }
      if (e.key !== "Tab" || !panel) return;
      const items = Array.from(panel.querySelectorAll(FOCUSABLE));
      if (!items.length) return;
      const firstEl = items[0], lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      if (opener && opener.focus) opener.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 120 }} />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}
        style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", background: T.panel, border: `1px solid ${T.border}`, borderRadius: 14, padding: 18, zIndex: 121, width: `min(${width}px, 94vw)`, maxHeight: "86vh", overflowY: "auto", textAlign: "left", boxShadow: "0 20px 60px rgba(0,0,0,0.35)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
          <h2 id={titleId} style={{ fontSize: 17, fontWeight: 800, color: T.text, margin: 0 }}>{title}</h2>
          {onClose && (
            <button className="rb-btn" type="button" onClick={onClose} aria-label={closeLabel}
              style={{ background: "none", border: "none", cursor: "pointer", color: T.text, padding: 6, borderRadius: 6 }}>
              <X size={18} />
            </button>
          )}
        </div>
        {children}
      </div>
    </>
  );
}

const fmtTime = (ts, lang) => (ts ? new Date(ts).toLocaleString(lang === "en" ? "en-GB" : "th-TH", { dateStyle: "medium", timeStyle: "short" }) : "—");

export { Button, Field, Notice, Modal, inputStyle, BTN, fmtTime };
